/**
 * What the API documentation says about one field.
 *
 * The generated OpenAPI contract cannot answer this. Its row schemas come from
 * `get_example_row_serializer_class`, which synthesises one fictional table --
 * so it describes rows in general and never *this* table, with these field ids,
 * these names and these select options. That is the whole reason a per-database
 * documentation page exists at all.
 *
 * No React and no translation happens here. A description is returned as the key
 * and the values to interpolate, so this layer stays a pure function of a field
 * and the page decides how to say it.
 */
import type { Field, FieldOf, FieldTypeName } from '@/api/types'
import { DURATION_FORMAT_INFO, isDurationFormat } from './duration'
import {
  FORMULA_TYPE_BEHAVES_AS,
  arrayItemIsWrapped,
  isArrayFormulaType,
  type ArrayFormulaType,
} from './formula-types'

/**
 * The sentence describing a field, before it is said in a language.
 *
 * The key set is closed and typed, so a description naming a key that has no
 * translation is a compile error rather than a `[missing "en..."]` string in the
 * middle of somebody's API reference.
 */
export type DocsDescriptionKey =
  | 'text'
  | 'longText'
  | 'linkRow'
  | 'number'
  | 'numberPositive'
  | 'decimal'
  | 'decimalPositive'
  | 'rating'
  | 'boolean'
  | 'date'
  | 'dateTime'
  | 'lastModified'
  | 'lastModifiedTime'
  | 'createdOn'
  | 'createdOnTime'
  | 'lastModifiedBy'
  | 'createdBy'
  | 'duration'
  | 'url'
  | 'email'
  | 'file'
  | 'singleSelect'
  | 'multipleSelect'
  | 'phoneNumber'
  | 'formula'
  | 'count'
  | 'rollup'
  | 'lookup'
  | 'multipleCollaborators'
  | 'uuid'
  | 'autonumber'
  | 'password'
  | 'formViewEditRow'

export interface DocsDescription {
  key: DocsDescriptionKey
  values?: Record<string, string | number>
}

export interface FieldDocs<T extends FieldTypeName = FieldTypeName> {
  /**
   * The type named in the field listing -- `string`, `number`, `array`.
   *
   * Not a JSON type and not the field type either: `decimal`, `duration`,
   * `uuid` and `autonumber` all arrive as one of the three, and saying so is
   * what tells somebody what to send.
   *
   * `null` for a field whose type depends on something the field does not say --
   * a formula whose expression has not been computed yet.
   */
  dataType: (field: FieldOf<T>) => string | null
  description: (field: FieldOf<T>) => DocsDescription
  requestExample: (field: FieldOf<T>) => unknown
  responseExample: (field: FieldOf<T>) => unknown
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

/** A type whose request and response look the same, described by one sentence. */
function simple<T extends FieldTypeName>(
  dataType: string,
  key: DocsDescriptionKey,
  example: unknown,
): FieldDocs<T> {
  return {
    dataType: () => dataType,
    description: () => ({ key }),
    requestExample: () => example,
    responseExample: () => example,
  }
}

/**
 * The file object the API answers with, and the shape a formula's file entry
 * takes too.
 *
 * One object rather than a list, because a file field answers with a list of
 * these and a `single_file` formula answers with exactly one.
 */
const FILE_NAME =
  'VXotniBOVm8tbstZkKsMKbj2Qg7KmPvn_39d354a76abe56baaf569ad87d0333f58ee4bf3eed368e3b9dc736fd18b09dfd.png'

export const FILE_RESPONSE = {
  url: `https://files.example.com/user_files/${FILE_NAME}`,
  thumbnails: {
    tiny: {
      url: `https://files.example.com/media/thumbnails/tiny/${FILE_NAME}`,
      width: 21,
      height: 21,
    },
    small: {
      url: `https://files.example.com/media/thumbnails/small/${FILE_NAME}`,
      width: 48,
      height: 48,
    },
  },
  name: FILE_NAME,
  size: 229940,
  mime_type: 'image/png',
  is_image: true,
  image_width: 1280,
  image_height: 585,
  uploaded_at: '2020-11-17T12:16:10.035234+00:00',
}

/**
 * A date field's example, which depends on whether it carries a time.
 *
 * Upstream's two sentences for this are swapped -- a date-only field is
 * described as accepting "a date time" and a field with a time as accepting "a
 * date" -- in the English strings and in all twenty translations of them. Named
 * here because the port says the true thing, and a reader comparing the two
 * pages would otherwise think this one had it wrong.
 */
function dateExample(field: { date_include_time?: boolean | null }): string {
  return field.date_include_time === true ? '2020-01-01T12:00:00Z' : '2020-01-01'
}

/** A field the backend writes and refuses to be told about. */
function readOnlyDate<T extends FieldTypeName>(
  withTime: DocsDescriptionKey,
  withoutTime: DocsDescriptionKey,
): FieldDocs<T> {
  return {
    // Null, not `'date'`: the row payload has no place for it, so naming a type
    // to send would be naming a type for a request that is refused.
    dataType: () => null,
    description: (field) => ({
      key:
        (field as { date_include_time?: boolean | null }).date_include_time === true
          ? withTime
          : withoutTime,
    }),
    requestExample: (field) => dateExample(field as { date_include_time?: boolean }),
    responseExample: (field) => dateExample(field as { date_include_time?: boolean }),
  }
}

const SELECT_OPTION = { id: 1, value: 'Option', color: 'light-blue' }
const USER = { id: 1, name: 'John' }

// ---------------------------------------------------------------------------
// The map
// ---------------------------------------------------------------------------

/**
 * One entry per field type.
 *
 * `satisfies` over the mapped type is what makes a twenty-eighth field type a
 * compile error here rather than an undocumented column on somebody's API
 * reference -- which is the failure that matters, because an absent row reads
 * as a field the endpoint does not accept.
 */
export const FIELD_DOCS = {
  text: simple('string', 'text', 'string'),
  long_text: simple('string', 'longText', 'string'),

  link_row: {
    dataType: () => 'array',
    description: (field) => ({
      key: 'linkRow',
      values: { table: field.link_row_table_id ?? 0 },
    }),
    requestExample: () => [1],
    responseExample: () => [{ id: 0, value: 'string' }],
  },

  number: {
    dataType: (field) => ((field.number_decimal_places ?? 0) > 0 ? 'decimal' : 'number'),
    description: (field) => {
      const places = field.number_decimal_places ?? 0
      const kind = places === 0 ? 'number' : 'decimal'
      // Negative numbers are the option, so the sentence names the restriction
      // when there is one rather than the permission when there is not.
      const key = (
        field.number_negative === true ? kind : `${kind}Positive`
      ) as DocsDescriptionKey
      return { key, values: { places } }
    },
    requestExample: (field) => {
      const places = field.number_decimal_places ?? 0
      // A decimal example carries the field's own scale, because that is the
      // thing a reader is checking: how many places this column will keep.
      return places > 0 ? `0.${'0'.repeat(places)}` : 0
    },
    responseExample: (field) => {
      const places = field.number_decimal_places ?? 0
      return places > 0 ? `0.${'0'.repeat(places)}` : 0
    },
  },

  rating: simple('number', 'rating', 3),
  boolean: simple('boolean', 'boolean', true),

  date: {
    dataType: () => 'date',
    description: (field) => ({
      key: field.date_include_time === true ? 'dateTime' : 'date',
    }),
    requestExample: (field) => dateExample(field),
    responseExample: (field) => dateExample(field),
  },

  last_modified: readOnlyDate('lastModifiedTime', 'lastModified'),
  created_on: readOnlyDate('createdOnTime', 'createdOn'),

  last_modified_by: {
    dataType: () => 'object',
    description: () => ({ key: 'lastModifiedBy' }),
    requestExample: () => USER,
    responseExample: () => USER,
  },
  created_by: {
    dataType: () => 'object',
    description: () => ({ key: 'createdBy' }),
    requestExample: () => USER,
    responseExample: () => USER,
  },

  duration: {
    dataType: () => 'duration',
    description: (field) => ({
      key: 'duration',
      values: { format: field.duration_format ?? '' },
    }),
    // The field's own format, because a duration accepts the text it renders --
    // an example in another format is an example this column would refuse.
    requestExample: (field) => durationExample(field.duration_format),
    responseExample: (field) => durationExample(field.duration_format),
  },

  url: simple('string', 'url', 'https://example.com'),
  email: simple('string', 'email', 'example@example.com'),

  file: {
    dataType: () => 'array',
    description: () => ({ key: 'file' }),
    // A name alone going in, the whole object coming back: the request names a
    // file already uploaded, and the answer describes it.
    requestExample: () => [{ name: FILE_NAME }],
    responseExample: () => [FILE_RESPONSE],
  },

  single_select: {
    dataType: () => 'number',
    description: () => ({ key: 'singleSelect' }),
    requestExample: () => 1,
    responseExample: () => SELECT_OPTION,
  },
  multiple_select: {
    dataType: () => 'array',
    description: () => ({ key: 'multipleSelect' }),
    requestExample: () => [1],
    responseExample: () => [SELECT_OPTION],
  },

  phone_number: simple('string', 'phoneNumber', '+1-541-754-3010'),

  formula: {
    dataType: (field) => formulaDataType(field),
    description: () => ({ key: 'formula' }),
    requestExample: () => READ_ONLY_REQUEST,
    responseExample: (field) => formulaResponseExample(field),
  },
  count: {
    dataType: (field) => formulaDataType(field),
    description: () => ({ key: 'count' }),
    requestExample: () => READ_ONLY_REQUEST,
    responseExample: (field) => formulaResponseExample(field),
  },
  rollup: {
    dataType: (field) => formulaDataType(field),
    description: () => ({ key: 'rollup' }),
    requestExample: () => READ_ONLY_REQUEST,
    responseExample: (field) => formulaResponseExample(field),
  },
  lookup: {
    dataType: (field) => formulaDataType(field),
    description: () => ({ key: 'lookup' }),
    requestExample: () => READ_ONLY_REQUEST,
    responseExample: (field) => formulaResponseExample(field),
  },

  multiple_collaborators: {
    dataType: () => 'array',
    description: () => ({ key: 'multipleCollaborators' }),
    requestExample: () => [{ id: 1 }],
    responseExample: () => [USER],
  },

  uuid: simple('uuid', 'uuid', '00000000-0000-0000-0000-000000000000'),
  autonumber: simple('autonumber', 'autonumber', 1),
  password: {
    dataType: () => 'boolean',
    description: () => ({ key: 'password' }),
    // A string going in and `true` coming back, which is what the sentence
    // beside it says. Upstream shows the string `'true'` for both, so its
    // request example is a value the endpoint would store as the password.
    requestExample: () => 'a-password',
    responseExample: () => true,
  },
  form_view_edit_row: simple(
    'string',
    'formViewEditRow',
    'https://example.com/form/slug/?edit_token=...',
  ),
} satisfies { [K in FieldTypeName]: FieldDocs<K> }

/**
 * What a request that includes a read-only field is told.
 *
 * A sentence rather than a value, because there is no value that would be
 * accepted: the endpoint refuses the key outright.
 */
export const READ_ONLY_REQUEST =
  'it is invalid to include request data for this field as it is read only'

function durationExample(format: unknown): string {
  return isDurationFormat(format) ? DURATION_FORMAT_INFO[format].example : '1:23'
}

/**
 * What a formula's result looks like, worked out from the type the backend
 * computed for it.
 *
 * The formula layer answers this: every formula type behaves as one of the
 * ordinary field types, so the example is that type's -- except an array, whose
 * entries are wrapped in `{ id, value }` unless they are files.
 */
function formulaResult(field: Field): {
  dataType: string | null
  responseExample: unknown
} {
  const shape = field as {
    formula_type?: string | null
    array_formula_type?: string | null
  }

  if (shape.formula_type === 'array') {
    const entry = shape.array_formula_type
    if (!isArrayFormulaType(entry)) return { dataType: 'array', responseExample: null }
    const value = formulaScalarExample(entry, field)
    return {
      dataType: 'array',
      responseExample: arrayItemIsWrapped(entry) ? [{ id: 0, value }] : [value],
    }
  }

  if (!isArrayFormulaType(shape.formula_type)) {
    return { dataType: null, responseExample: null }
  }
  return {
    dataType: formulaScalarDataType(shape.formula_type, field),
    responseExample: formulaScalarExample(shape.formula_type, field),
  }
}

/**
 * One formula value, as the type it behaves like would answer.
 *
 * A file is the one exception, and it is an exception in both places this is
 * called from: a file *field* answers with a list of files, while a
 * `single_file` formula -- whether alone or as an array entry -- answers with
 * exactly one. Borrowing the field type's example wholesale would document a
 * list of lists.
 */
function formulaScalarExample(entryType: ArrayFormulaType, field: Field): unknown {
  if (entryType === 'single_file') return FILE_RESPONSE
  return docsFor(FORMULA_TYPE_BEHAVES_AS[entryType]).responseExample(field)
}

function formulaScalarDataType(entryType: ArrayFormulaType, field: Field): string {
  if (entryType === 'single_file') return 'single_file'
  return docsFor(FORMULA_TYPE_BEHAVES_AS[entryType]).dataType(field) ?? ''
}

function formulaDataType(field: Field): string | null {
  return formulaResult(field).dataType
}

function formulaResponseExample(field: Field): unknown {
  return formulaResult(field).responseExample
}

/** The documentation for one field type, without narrowing at the call site. */
export function docsFor(type: FieldTypeName): FieldDocs {
  return FIELD_DOCS[type] as FieldDocs
}

/**
 * The field listing's own example -- the object the fields endpoint answers
 * with, rather than a row value.
 */
export function fieldResponseExample(field: Field): Record<string, unknown> {
  return {
    id: field.id,
    table_id: field.table_id,
    name: field.name,
    order: field.order,
    type: field.type,
    primary: field.primary,
    read_only: field.read_only,
    description: field.description ?? 'A sample description',
  }
}
